import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import BuzzTicketLogo from '../../assets/BuzzTicketLogo.png';
import './ForgotPassword.css';

const API = '/api/shared';
const RESEND_COOLDOWN = 5 * 60; // seconds

const STEP = { EMAIL: 1, CODE: 2, PASSWORD: 3, DONE: 4 };

export default function ForgotPassword() {
  const navigate = useNavigate();

  const [step, setStep] = useState(STEP.EMAIL);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userId, setUserId] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [codeExpired, setCodeExpired] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(RESEND_COOLDOWN);
  const countdownRef = useRef(null);

  function startCountdown() {
    setResendCountdown(RESEND_COOLDOWN);
    clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  useEffect(() => {
    if (step === STEP.CODE) startCountdown();
    if (step !== STEP.CODE) clearInterval(countdownRef.current);
    return () => clearInterval(countdownRef.current);
  }, [step]);

  const clearError = () => setError('');

  // ── Step 1: Send code ──────────────────────────────────────────────────────
  async function handleSendCode(e) {
    e.preventDefault();
    clearError();
    setLoading(true);
    try {
      const res = await fetch(`${API}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) return setError(data.message || 'Something went wrong.');
      setUserId(data.userId);
      setStep(STEP.CODE);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // ── Step 2: Verify code ────────────────────────────────────────────────────
  async function handleVerifyCode(e) {
    e.preventDefault();
    clearError();
    setCodeExpired(false);
    setLoading(true);
    try {
      const res = await fetch(`${API}/verify-reset-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, code }),
      });
      const data = await res.json();
      if (res.status === 410) {
        setCodeExpired(true);
        return setError(data.message);
      }
      if (!res.ok) return setError(data.message || 'Something went wrong.');
      setStep(STEP.PASSWORD);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // ── Resend code ────────────────────────────────────────────────────────────
  async function handleResend() {
    clearError();
    setCodeExpired(false);
    setCode('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) return setError(data.message || 'Something went wrong.');
      setUserId(data.userId);
      startCountdown();
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // ── Step 3: Reset password ─────────────────────────────────────────────────
  async function handleResetPassword(e) {
    e.preventDefault();
    clearError();
    if (newPassword !== confirmPassword) return setError('Passwords do not match.');
    if (newPassword.length < 6) return setError('Password must be at least 6 characters.');
    setLoading(true);
    try {
      const res = await fetch(`${API}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, code, newPassword }),
      });
      const data = await res.json();
      if (res.status === 410) {
        setStep(STEP.CODE);
        setCodeExpired(true);
        return setError('Your session expired. Please enter a new code.');
      }
      if (!res.ok) return setError(data.message || 'Something went wrong.');
      setStep(STEP.DONE);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fp-page">
      <div className="fp-card">
        <img src={BuzzTicketLogo} alt="BuzzTicket" className="fp-logo" />

        {step !== STEP.DONE && (
          <div className="fp-steps">
            {[STEP.EMAIL, STEP.CODE, STEP.PASSWORD].map((s) => (
              <div key={s} className={`fp-step-dot ${step >= s ? 'fp-step-dot--active' : ''}`} />
            ))}
          </div>
        )}

        {/* ── Step 1: Email ── */}
        {step === STEP.EMAIL && (
          <>
            <h2 className="fp-title">Forgot Password</h2>
            <p className="fp-subtitle">Enter the email address linked to your account.</p>

            {error && <div className="fp-error">{error}</div>}

            <form onSubmit={handleSendCode} className="fp-form">
              <label className="fp-label">Email Address</label>
              <input
                type="email"
                className="fp-input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
              <button type="submit" className="fp-btn" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Code'}
              </button>
            </form>

            <button className="fp-back" onClick={() => navigate('/')}>Back to Sign In</button>
          </>
        )}

        {/* ── Step 2: Code ── */}
        {step === STEP.CODE && (
          <>
            <h2 className="fp-title">Enter Your Code</h2>
            <p className="fp-subtitle">
              A 6-character code was sent to <strong>{email}</strong>. It expires in 5 minutes.
            </p>

            {error && <div className="fp-error">{error}</div>}

            <form onSubmit={handleVerifyCode} className="fp-form">
              <label className="fp-label">Reset Code</label>
              <input
                type="text"
                className="fp-input fp-input--code"
                placeholder="A1B2C3"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                maxLength={6}
                required
                autoFocus
              />
              <button type="submit" className="fp-btn" disabled={loading}>
                {loading ? 'Verifying...' : 'Verify Code'}
              </button>
            </form>

            {resendCountdown > 0 ? (
              <p className="fp-resend-hint">
                Resend available in {Math.floor(resendCountdown / 60)}:{String(resendCountdown % 60).padStart(2, '0')}
              </p>
            ) : (
              <button className="fp-resend" onClick={handleResend} disabled={loading}>
                {loading ? 'Sending...' : 'Resend Code'}
              </button>
            )}

            <button className="fp-back" onClick={() => { setStep(STEP.EMAIL); clearError(); setCodeExpired(false); }}>
              Back
            </button>
          </>
        )}

        {/* ── Step 3: New password ── */}
        {step === STEP.PASSWORD && (
          <>
            <h2 className="fp-title">Set New Password</h2>
            <p className="fp-subtitle">Choose a new password for your account.</p>

            {error && <div className="fp-error">{error}</div>}

            <form onSubmit={handleResetPassword} className="fp-form">
              <label className="fp-label">New Password</label>
              <input
                type="password"
                className="fp-input"
                placeholder="At least 6 characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoFocus
              />
              <label className="fp-label">Confirm New Password</label>
              <input
                type="password"
                className="fp-input"
                placeholder="Retype your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <button type="submit" className="fp-btn" disabled={loading}>
                {loading ? 'Saving...' : 'Save Password'}
              </button>
            </form>
          </>
        )}

        {/* ── Done ── */}
        {step === STEP.DONE && (
          <div className="fp-done">
            <div className="fp-done__icon">✓</div>
            <h2 className="fp-title">Password Reset!</h2>
            <p className="fp-subtitle">Your password has been updated. You can now sign in with your new password.</p>
            <button className="fp-btn" onClick={() => navigate('/')}>Back to Sign In</button>
          </div>
        )}
      </div>
    </div>
  );
}
